import React from 'react';
import { LinkModel } from '../../Types/LinkModel';
import styled from 'styled-components';
import { breakpoint } from '../../styling/mixins/breakpoints';
import { cells, grid } from '../../styling/utils/grid';
import { btn } from '../../styling/mixins/buttons';
import { fontFamilyBold } from '../../styling/variables/typography';

interface LinkBlockProps {
  links: LinkBlockItem[];
  title?: string;
  allowThumbnails: boolean;
}

interface LinkBlockItem {
  thumbnailUrl?: string;
  link: LinkModel;
}

interface LinkBlockWrapperProps {
  $linksCount: number;
  $allowThumbnails: boolean;
}

const StyledWrapper = styled.div<LinkBlockWrapperProps>`
  display: flex;
  overflow-x: auto;
  overflow-y: hidden;
  -ms-overflow-style: none;
  scrollbar-width: none;
  padding-right: 3rem;
  gap: 1.6rem;
  position: relative;

  ${breakpoint('medium')} {
    ${(p) => cells(Math.min(p.$linksCount, 6))}
    padding-right: unset;
    display: grid;
    -ms-overflow-style: unset;
    scrollbar-width: unset;
    overflow: unset;
  }

  &:after {
    content: '';
    width: 40px;
    height: 100%;
    right: 0;
    pointer-events: none;
    position: absolute;
    background: linear-gradient(
      270deg,
      rgba(255, 255, 255, 1) 0%,
      rgba(255, 255, 255, 0) 100%
    );

    ${breakpoint('medium')} {
      display: none;
    }
  }
`;

const StyledLink = styled.a<{ $allowThumbnails: boolean }>`
  ${grid};
  ${(p) => (!p.$allowThumbnails ? btn('outlined', 'large') : null)};
  flex: ${(p) => (!p.$allowThumbnails ? 1 : null)};
  display: flex;
  text-align: center;
  align-items: center;
  ${fontFamilyBold}
  text-align: center;
  align-items: center;
  flex-direction: column;

  ${breakpoint('medium')} {
    white-space: unset;
  }

  img {
    aspect-ratio: 1/1;
    min-width: 128px;
    object-fit: cover;
    max-height: 248px;
    width: 100%;
  }

  span {
    white-space: nowrap;
  }
`;

const StyledHeader = styled.h2`
  margin-bottom: 1.6rem;

  ${breakpoint('medium')} {
    margin-bottom: 2.4rem;
  }
`;

const LinkBlock = ({ links, title, allowThumbnails }: LinkBlockProps) => {
  const Link = React.memo<{ item: LinkBlockItem }>(({ item }) => (
    <StyledLink $allowThumbnails={allowThumbnails} href={item.link.href}>
      {allowThumbnails && item.thumbnailUrl && (
        <img src={item.thumbnailUrl} alt={item.link.text} loading="lazy" />
      )}
      <span>{item.link.text}</span>
    </StyledLink>
  ));

  return (
    <div className="section">
      <StyledHeader>{title}</StyledHeader>
      <StyledWrapper
        $linksCount={links.length}
        $allowThumbnails={allowThumbnails}
      >
        {links.map((item, idx) => (
          <Link item={item} key={'link-block' + idx + item.link.text} />
        ))}
      </StyledWrapper>
    </div>
  );
};

export default LinkBlock;
